"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useEffect } from "react";
import { useAuthClient } from "@/lib/auth-client";
import { getAuthPagePath } from "@/lib/auth-redirect";
import { useApi } from "@/lib/fetch-client";
import type { Route } from "next";
export interface UserUpdateData {
  name?: string;
  email?: string;
}
export interface PasswordUpdateData {
  currentPassword: string;
  newPassword: string;
}
export interface UseUserOptions {
  redirectTo?: string;
  redirectWhen?: "authenticated" | "unauthenticated";
  checkOnboarding?: boolean;
  enabled?: boolean;
}

/**
 * Lightweight session check for marketing/auth surfaces. Uses better-auth's
 * get-session endpoint, which returns 200 with null for anonymous visitors,
 * so it never logs a 401 console error like /user/me does.
 */
export function useSessionStatus() {
  const $ = _c(7);
  const authClient = useAuthClient();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["auth-session-status"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== authClient) {
    t1 = {
      queryKey: t0,
      queryFn: async () => {
        const {
          data: session
        } = await authClient.getSession();
        return session ?? null;
      },
      retry: 0,
      staleTime: 60000,
      refetchOnWindowFocus: false
    };
    $[1] = authClient;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data,
    isLoading
  } = useQuery(t1);
  const t2 = !!data?.user;
  const t3 = data ?? null;
  let t4;
  if ($[3] !== isLoading || $[4] !== t2 || $[5] !== t3) {
    t4 = {
      isAuthenticated: t2,
      isLoading,
      session: t3
    };
    $[3] = isLoading;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
export function useUser(options?: UseUserOptions) {
  const posthog = usePostHog();
  const router = useRouter();
  const api = useApi();
  const {
    data,
    isLoading,
    error
  } = api.useQuery("get", "/user/me", {}, {
    retry: 0,
    staleTime: 5 * 60 * 1000,
    // 5 minutes
    refetchOnWindowFocus: false,
    enabled: options?.enabled ?? true
  });
  useEffect(() => {
    if (data) {
      posthog.identify(data.user.id, {
        email: data.user.email,
        name: data.user.name,
        onboarding_completed: data.user.onboardingCompleted
      });
    }
  }, [data, posthog]);
  const redirectTo = options?.redirectTo;
  const redirectWhen = options?.redirectWhen;
  const checkOnboarding = options?.checkOnboarding;

  // Handle existing redirect logic
  useEffect(() => {
    if (!redirectTo || !redirectWhen) {
      return;
    }
    const hasUser = !!data?.user;
    if (redirectWhen === "authenticated" && hasUser && !isLoading && !error) {
      if (checkOnboarding && !data.user.onboardingCompleted) {
        router.push(getAuthPagePath("/onboarding", redirectTo) as Route);
      } else {
        router.push(redirectTo as Route);
      }
    } else if (redirectWhen === "unauthenticated" && !isLoading && (!hasUser || error)) {
      router.push(redirectTo as Route);
    }
  }, [data?.user, isLoading, error, router, redirectTo, redirectWhen, checkOnboarding]);
  return {
    user: data?.user ?? null,
    isLoading,
    error,
    data
  };
}
export function useUpdateUser() {
  const queryClient = useQueryClient();
  const api = useApi();
  const userQueryKey = api.queryOptions("get", "/user/me", {}).queryKey;
  return api.useMutation("patch", "/user/me", {
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: userQueryKey
      });
      void queryClient.invalidateQueries({
        queryKey: ["session"]
      });
    }
  });
}
export function useUpdatePassword() {
  const api = useApi();
  return api.useMutation("put", "/user/password");
}
export function useDeleteAccount() {
  const api = useApi();
  return api.useMutation("delete", "/user/me");
}

/**
 * What deleting the account will tear down: the organizations the user is the
 * last member of, and the Stripe subscriptions those organizations still hold.
 * Fetched so the confirmation dialog can name them instead of deleting billing
 * silently.
 */
export function useAccountDeletionPreview(enabled = true) {
  const api = useApi();
  return api.useQuery("get", "/user/me/deletion-preview", {}, {
    enabled,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false
  });
}